/** Golden bottles from a run distance on: what their question pays and how hard it is. */
export interface QuizRound {
  /** Run distance (m) this round starts at – it holds until the next round's distance. */
  readonly fromDistance: number;
  /** Bonus bottles at the start of the clock – they run down linearly to 0 over the time limit. */
  readonly bonus: number;
  /** Question difficulty (1 easy … 3 hard); without such questions in the pool the nearest level is asked. */
  readonly difficulty: number;
}

/**
 * Bau-Quiz at the golden bottle (docs/quiz/QUIZ_SYSTEM.md): the picture freezes, a random multiple-choice
 * question from the pool appears and a bonus of bottles counts down with the answering time – a correct answer
 * banks what is left of it.
 */
export interface QuizConfig {
  /** Question pool (JSON) – today a static file, later delivered by the backend in the same format. */
  readonly questionsUrl: string;
  /** Seconds to answer. */
  readonly timeLimitSeconds: number;
  /**
   * Rounds by run distance, ascending (the golden bottles come every 500 m, from 2000 m every 1000 m): bonus and
   * difficulty rise with the distance and the speed (user 2026-10-02: "je nach Steigerung … mehr Bierflaschen",
   * "die Schwierigkeit soll auch steigen"; "ab zweitausend Meter", when it gets faster).
   */
  readonly rounds: readonly QuizRound[];
  /**
   * Extra lives one quiz bonus may buy at most – the bottles past that count for the score only (user 2026-10-02,
   * "Variante 2": otherwise a single answer fills every life at once).
   */
  readonly maxExtraLives: number;
  /**
   * Bottles taken off for a wrong answer or a timeout – and for a mini game at the golden bottle that reached nothing
   * (user 2026-10-04: "wenn er verfehlt oder es ist was falsch, werden 100 Flaschen abgezogen"). Never below zero.
   */
  readonly wrongPenalty: number;
  /** The bonus counter moves in steps of this many bottles. */
  readonly bonusStep: number;
  /** Seconds the result (right / wrong, the correct answer) stays before the run goes on. */
  readonly resultSeconds: number;
  /** Answers per question (multiple choice). */
  readonly answerCount: number;
  /** Text limits of the pool check – a question has to be read and answered within seconds on a phone. */
  readonly maxQuestionLength: number;
  readonly maxAnswerLength: number;
  /** CSS filter on the 3D canvas while the picture is frozen. */
  readonly freezeFilter: string;
  /**
   * Keys answering A, B, C at once (besides tapping the buttons). The arrow keys only move the marker and Enter / Space
   * confirms (QuizOverlay, user 2026-10-04) – so they are not in here.
   */
  readonly answerKeys: readonly (readonly string[])[];
}

export const QUIZ_CONFIG: QuizConfig = {
  questionsUrl: '/quiz/bau-quiz.de.json',
  timeLimitSeconds: 7, // 5 until 2026-10-04 (user: "bei der Antwort ein wenig mehr Zeit, 1–2 Sekunden")
  rounds: [
    // at most 100 bottles for the quiz and every mini game, shared out by success (user 2026-10-04 – was 500/750/1000)
    { fromDistance: 0, bonus: 100, difficulty: 1 },
    { fromDistance: 2000, bonus: 100, difficulty: 2 },
    { fromDistance: 4000, bonus: 100, difficulty: 3 },
  ],
  maxExtraLives: 1,
  wrongPenalty: 100,
  bonusStep: 1,
  resultSeconds: 1.8,
  answerCount: 3,
  maxQuestionLength: 110,
  maxAnswerLength: 40,
  freezeFilter: 'saturate(0.35) brightness(0.55) blur(1.5px)',
  answerKeys: [
    ['1', 'a'],
    ['2', 'b'],
    ['3', 'c'],
  ],
};
